import React, { useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import 'mathlive';

const MathInsertDialog = ({ open, onClose, onInsert }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const mathFieldRef = useRef(null);
    const [latex, setLatex] = useState('');
    const [mathType, setMathType] = useState('inline'); // 'inline' or 'display'

    const handleInsert = () => {
        if (mathFieldRef.current) {
            const latexValue = mathFieldRef.current.value;
            // Wrap with appropriate delimiters
            const wrappedLatex = mathType === 'inline'
                ? `\\(${latexValue}\\)`
                : `\\[${latexValue}\\]`;
            onInsert(wrappedLatex);
            // Clear the field for next use
            mathFieldRef.current.value = '';
            setLatex('');
            setMathType('inline'); // Reset to default
        }
        onClose();
    };

    const handleCancel = () => {
        // Clear the field
        if (mathFieldRef.current) {
            mathFieldRef.current.value = '';
            setLatex('');
            setMathType('inline'); // Reset to default
        }
        onClose();
    };

    const handleMathFieldInput = (evt) => {
        setLatex(evt.target.value);
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Insert Mathematical Expression</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Type or use the virtual keyboard to create your mathematical expression:
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                            Expression type:
                        </Typography>
                        <ToggleButtonGroup
                            value={mathType}
                            exclusive
                            onChange={(e, newType) => {
                                if (newType !== null) {
                                    setMathType(newType);
                                }
                            }}
                            size="small"
                        >
                            <ToggleButton value="inline">
                                Inline (\\( \\))
                            </ToggleButton>
                            <ToggleButton value="display">
                                Display Block (\\[ \\])
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Box
                        sx={{
                            border: `2px solid ${colors.primary[300]}`,
                            borderRadius: '4px',
                            padding: '16px',
                            backgroundColor: colors.primary[400],
                            minHeight: '120px',
                        }}
                    >
                        <math-field
                            ref={mathFieldRef}
                            onInput={handleMathFieldInput}
                            style={{
                                width: '100%',
                                border: 'none',
                                fontSize: '20px',
                                minHeight: '100px',
                            }}
                        />
                    </Box>
                    {latex && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                                LaTeX code: <code>{mathType === 'inline' ? `\\(${latex}\\)` : `\\[${latex}\\]`}</code>
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleInsert}
                    variant="contained"
                    color="secondary"
                    disabled={!latex}
                >
                    Insert
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MathInsertDialog;
